import { ChangeDetectionStrategy, Component, ElementRef, inject, Input, signal, Signal, Output, EventEmitter } from '@angular/core';
import { CommonModule, NgClass } from '@angular/common';
import { SvgIconComponent } from '@ngneat/svg-icon';
import { ScheduleCalendarElement } from '@libs/store';
import { TranslateModule } from '@ngx-translate/core';
import { AppointmentStatusCode, AppointmentTimePattern } from '@ffd/api-client';
import { TooltipModule } from '@libs/ui-lib';
import { CdkMenuModule } from '@angular/cdk/menu';
import { TagEvent } from '@libs/shared-services';
import { TimeRangeFormatPipe } from '../../time-range-format';
import { ScheduleCalendarItemTooltipComponent } from '../schedule-calendar-item-tooltip';
import { getStatusColorHex } from '../appointment-status-chip';
import { AppointmentContextMenuComponent } from '../appointment-context-menu';

const COMPLETED_STATUS_ID = -106;

@Component({
  selector: 'lib-schedule-calendar-item',
  templateUrl: './schedule-calendar-item.component.html',
  styleUrl: './schedule-calendar-item.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgClass,
    SvgIconComponent,
    TranslateModule,
    CommonModule,
    TooltipModule,
    CdkMenuModule,
    TimeRangeFormatPipe,
    ScheduleCalendarItemTooltipComponent,
    AppointmentContextMenuComponent,
  ],
})
export class ScheduleCalendarItemComponent {
  @Input({ required: true }) public vm!: ScheduleCalendarElement;
  @Input() public isMyDay: boolean = false;
  @Input() public isNewAppointmentItem: boolean = false;
  @Input() public isShowcase: boolean = false;
  @Input() public displayHourglass: boolean = true;
  @Input() public isCompactViewSignal: Signal<boolean> = signal(false);
  @Input() public statusOptions: AppointmentStatusCode[] = [];
  @Input() public areTreatmentsLoaded: boolean = false;

  @Output() public setComplete = new EventEmitter<ScheduleCalendarElement>();
  @Output() public editStatus = new EventEmitter<ScheduleCalendarElement>();
  @Output() public editAppointment = new EventEmitter<ScheduleCalendarElement>();
  @Output() public deleteAppointment = new EventEmitter<ScheduleCalendarElement>();
  @Output() public openChart = new EventEmitter<ScheduleCalendarElement>();
  @Output() public openHealthHistory = new EventEmitter<ScheduleCalendarElement>();
  @Output() public changeStatus = new EventEmitter<{ element: ScheduleCalendarElement; statusId: number }>();
  @Output() public rightClick = new EventEmitter<ScheduleCalendarElement>();

  protected TagEvent = TagEvent;

  public get isCompleted(): boolean {
    return this.vm?.statusId === COMPLETED_STATUS_ID;
  }

  public readonly elementRef = inject(ElementRef);
  public AppointmentTimePattern = AppointmentTimePattern;

  protected getStatusColorHex(): string | null {
    return getStatusColorHex(this.vm.statusColor);
  }

  public onSetComplete(): void {
    this.setComplete.emit(this.vm);
  }

  public onEditStatus(): void {
    this.editStatus.emit(this.vm);
  }

  public onEditAppointment(): void {
    this.editAppointment.emit(this.vm);
  }

  public onDeleteAppointment(): void {
    this.deleteAppointment.emit(this.vm);
  }

  public onOpenHealthHistory(): void {
    this.openHealthHistory.emit(this.vm);
  }

  public onChangeStatus(statusId: number): void {
    this.changeStatus.emit({ element: this.vm, statusId });
  }

  public onRightClick(): void {
    if (this.isNewAppointmentItem) return;
    this.rightClick.emit(this.vm);
  }
}
